//Imports
import React, { useCallback, useEffect, useState } from "react";

//Controls
import {
    Button,
    Modal,
    Form,
    Select,
    TextArea
} from "@gpstrackit/gtc-react-controls";

//Components
import {
    NotificationManager,
    NotificationContainer,
} from "Components/Notifications";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Actions
import { workOrderChangeStatusWorkOrder, reset, workOrdersChangeStatusSearchStatuses } from "./redux/slice";

//Styles
import "./index.scss";

const ChangeStatus = ({ refresh }) => {

    //Redux States
    const dispatch = useDispatch();
    const { open, loading, result, error, workOrder, loadingWorkOrderStatuses, workOrderStatuses } = useSelector((state) => state.workOrdersChangeStatusRedux);
    const changeStatusFormRef = Form.useForm();

    //Local States
    const [formData, setFormData] = useState({ status: null, description: null });
    const [noteError, setNoteError] = useState("");

    //Language
    const { messages } = useIntl();

    //Handlers and utils
    const handleClose = useCallback(() => dispatch(reset()), []);

    //Effects
    useEffect(() => {
        if (error) {
            let message = error?.error || messages["errorMessage"];
            NotificationManager.error(
                message,
                messages["error"],
                3000
            );
        }
    }, [error]);

    useEffect(() => {
        if (open) {
            setNoteError("");
            dispatch(workOrdersChangeStatusSearchStatuses({}));
        }
    }, [open]);

    useEffect(() => {
        if (!result) return;
        if (result?.changeStatus) {
            NotificationManager.success(
                messages["workOrder_changeStatus_successMessage"],
                messages["success"],
                3000
            );
            refresh && refresh();
            dispatch(reset());
        } else {
            NotificationManager.error(
                messages["errorMessage"],
                messages["error"],
                3000
            );
            dispatch(reset({ result: null }));
        }
    }, [result]);

    useEffect(() => {
        if (workOrder && workOrderStatuses?.length) {
            let currentStatus = workOrderStatuses.find(status => parseInt(status.id) === parseInt(workOrder.statusId));
            if (currentStatus) {
                setTimeout(() => {
                    changeStatusFormRef.setValues({
                        status: { id: currentStatus?.id, name: currentStatus?.name },
                        note: workOrder?.description || "",
                    });
                    setFormData({
                        ...formData,
                        status: { id: currentStatus?.id, name: currentStatus?.name },
                        note: workOrder?.description || "",
                    });
                }, 500);
            }
        }
    }, [workOrderStatuses, workOrder]);

    const applyChangeStatus = useCallback(() => {
        let payload = {
            id: workOrder?.id,
            statusId: formData?.status?.id,
            note: formData?.note
        }
        dispatch(workOrderChangeStatusWorkOrder(payload));
    }, [formData]);

    return (
        <Modal
            title={`${messages['workOrder_changeStatus_title']} "${workOrder?.workOrderNumber}"`}
            size="small"
            open={open}
            handleClose={handleClose}
            loading={loading || loadingWorkOrderStatuses}
            footer={
                <div>
                    <Button
                        variant="secondary"
                        style={{ marginRight: "5px" }}
                        onClick={() => handleClose()}
                        isDisabled={loading}
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => { changeStatusFormRef?.submit() }}
                        isDisabled={loading || (formData?.status?.id === workOrder?.statusId)}
                    >
                        {messages["workOrder_changeStatus_change"]}
                    </Button>
                </div>
            }
        >
            <div style={{ padding: "10px 25px" }}>
                <Form
                    ref={changeStatusFormRef?.ref}
                    loading={loading}
                    onValidSubmit={(values) => {
                        const note = values?.note ?? formData?.note;
                        if (note == null || String(note).trim() === '') {
                            setNoteError(messages["required_field"] || "required_field");
                            return;
                        }
                        setNoteError("");
                        applyChangeStatus();
                    }}
                    onChangeValues={(values) => {
                        setFormData(values);
                        setNoteError("");
                    }}
                >
                    <Form.Item
                        name="status"
                        label={messages["workOrder_changeStatus_status"]}
                        rules={[{ required: true, message: 'required_field' }]}
                    >
                        <Select
                            id="status"
                            options={workOrderStatuses || []}
                            placeholder={messages["workOrder_changeStatus_status"]}
                            valueField="id"
                            textField="name"
                            style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                            isSearchable
                            isLoading={loadingWorkOrderStatuses}
                        />
                    </Form.Item>
                    <Form.Item
                        name="note"
                        label={messages["workOrder_changeStatus_note"]}
                        className={noteError ? "change-status-note-no-margin" : undefined}
                        rules={[
                            {
                                required: true,
                                message: 'required_field',
                                validator: (_, value) => {
                                    if (value == null || String(value).trim() === '') {
                                        return Promise.reject(new Error('required_field'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <TextArea
                            id="note"
                            maxLength={500}
                            className={noteError ? "is-invalid" : undefined}
                        />
                    </Form.Item>
                    {noteError && (
                        <label className="assistive-gtc-rc-text assistive-gtc-rc-text-error" style={{ marginTop: 5 }}>
                            {noteError}
                        </label>
                    )}
                </Form>
            </div>
            <NotificationContainer />
        </Modal>
    );
};

export default ChangeStatus;